import { SerpLensResponse } from '@dupe/common/types'

export interface KeywordMapping {
  positive: string[]
  negative: string[]
}

// Helper to filter out items that may look similar but are not the same & using the title to determine is it should be excluded
export const keywordMappings: KeywordMapping[] = [
  // Positive keywords determine similar keywords we should check for
  // These will match if the result product title includes any of these keywords
  // If the visual match result titles include any of these positive keywords it will return true
  // Otherwise if it contains a negative keyword it will return false
  {
    positive: ['coffee table', 'cocktail table', 'center table'],
    negative: [
      'dining table',
      'workstation',
      'standing desk',
      'office desk',
      'console table',
      'sofa table',
      'tv stand',
      'nightstand',
      'sideboard',
      'sink',
    ],
  },
  {
    positive: [
      'office desk',
      'executive desk',
      'computer desk',
      'writing desk',
    ],
    negative: [
      'dining table',
      'coffee table',
      'workstation',
      'standing desk',
      'side table',
      'end table',
      'console table',
      'drafting table',
      'gaming desk',
      'dressing table',
      'makeup table',
    ],
  },
  {
    positive: [
      'side table',
      'end table',
      'nightstand',
      'bedside table',
      'lamp table',
    ],
    negative: [
      'coffee table',
      'dining table',
      'workstation',
      'standing desk',
      'office desk',
      'planter',
      'vase',
      'console table',
      'tv stand',
      'plant stand',
    ],
  },
  {
    positive: ['ottoman', 'footstool', 'pouffe'],
    negative: ['bench', 'stool', 'chair', 'coffee table', 'storage bench'],
  },
  {
    positive: ['planter', 'plant pot', 'plant stand', 'flower pot'],
    negative: [
      'coffee table',
      'dining table',
      'workstation',
      'standing desk',
      'office desk',
      'side table',
      'end table',
      'basket',
      'vase',
      'lamp stand',
      'garden table',
    ],
  },
  {
    positive: ['bench', 'entryway bench', 'storage bench', 'upholstered bench'],
    negative: ['stool', 'ottoman', 'sofa', 'chaise lounge'],
  },
  {
    positive: [
      'tv stand',
      'media console',
      'media unit',
      'entertainment center',
    ],
    negative: [
      'coffee table',
      'console table',
      'sideboard',
      'bookshelf',
      'dresser',
    ],
  },
  {
    positive: ['dining table', 'kitchen table', 'breakfast table'],
    negative: ['coffee table', 'workbench', 'picnic table', 'craft table'],
  },
  {
    positive: [
      'bookshelf',
      'bookcase',
      'display shelf',
      'library shelf',
      'ladder shelf',
    ],
    negative: ['cabinet', 'console', 'sideboard', 'buffet'],
  },
  {
    positive: ['armchair', 'lounge chair', 'recliner', 'accent chair'],
    negative: ['sofa', 'loveseat', 'bench', 'ottoman'],
  },
  {
    positive: ['coffee table'],
    negative: ['dining table', 'workstation', 'standing desk', 'office desk'],
  },
  {
    positive: ['office desk'],
    negative: [
      'dining table',
      'coffee table',
      'workstation',
      'standing desk',
      'side table',
      'end table',
    ],
  },
  {
    positive: ['sofa', 'couch', 'sectional'],
    negative: ['loveseat', 'daybed', 'chaise', 'futon', 'bench'],
  },
  {
    positive: ['loveseat', 'two-seater'],
    negative: ['sofa', 'couch', 'sectional', 'armchair', 'bench'],
  },
  {
    positive: ['chaise lounge', 'chaise', 'lounger'],
    negative: ['sofa', 'loveseat', 'daybed', 'bench', 'futon'],
  },
  {
    positive: ['dresser', 'chest of drawers', 'bureau'],
    negative: ['sideboard', 'buffet', 'console', 'credenza', 'cabinet'],
  },
  {
    positive: ['sideboard', 'buffet', 'credenza'],
    negative: ['dresser', 'console table', 'tv stand', 'cabinet'],
  },
  {
    positive: ['recliner', 'reclining chair', 'lounger'],
    negative: ['armchair', 'accent chair', 'sofa', 'loveseat'],
  },
  {
    positive: ['cabinet', 'storage cabinet', 'display cabinet'],
    negative: ['bookshelf', 'bookcase', 'sideboard', 'buffet'],
  },
  {
    positive: ['console table', 'entryway table', 'hall table'],
    negative: [
      'tv stand',
      'media console',
      'sideboard',
      'buffet',
      'dining table',
    ],
  },
  {
    positive: ['bar stool', 'counter stool', 'pub stool'],
    negative: ['dining chair', 'side chair', 'office chair', 'armchair'],
  },
  {
    positive: ['vanity', 'makeup table', 'dressing table'],
    negative: ['desk', 'workstation', 'writing desk', 'office desk'],
  },
  {
    positive: ['shoe rack', 'shoe organizer', 'shoe shelf'],
    negative: ['bookshelf', 'bookcase', 'cabinet', 'console'],
  },
  {
    positive: ['storage bench', 'entryway bench', 'bench with storage'],
    negative: ['ottoman', 'footstool', 'pouffe'],
  },
]

export const filterProductsByTitle = (
  mainTitle: string,
  matches: SerpLensResponse['visual_matches']
): SerpLensResponse['visual_matches'] => {
  const mainTitleLower = mainTitle.toLowerCase()

  const matchingKeywordMappings = keywordMappings.filter((mapping) => {
    return mapping.positive.some((keyword) => {
      return mainTitleLower.includes(keyword.toLowerCase())
    })
  })

  if (matchingKeywordMappings.length === 0) {
    // if no keyword mapping found, return all matches
    return matches
  }

  // Adjust matches based on the keyword mappings
  const adjustedMatches = matches.map(
    (match: SerpLensResponse['visual_matches'][0]) => {
      const title = (match?.title || '').toLowerCase()

      const containsPositive = matchingKeywordMappings.some((mapping) =>
        mapping.positive.some((keyword) =>
          title.includes(keyword.toLowerCase())
        )
      )

      const containsNegative = matchingKeywordMappings.some((mapping) =>
        mapping.negative.some((keyword) =>
          title.includes(keyword.toLowerCase())
        )
      )

      // if title contains a positive, set exclude_by_title to false even if it also contains a negative
      if (containsPositive) {
        return { ...match, exclude_by_title: false }
      }

      if (containsNegative) {
        return { ...match, exclude_by_title: true }
      }

      return { ...match, exclude_by_title: false }
    }
  )

  return adjustedMatches
}
